import WebGLGroup from '../_app/cuchillo/3D/WebGLGroup';
import ModelGLB from '../_app/cuchillo/3D/ModelGLB';
import TexturesController from '../_app/cuchillo/3D/TexturesController';
import GeometryController from '../_app/cuchillo/3D/GeometryController';
import { Basics, isTouch } from '../_app/cuchillo/core/Basics';
import { Interaction } from '../_app/cuchillo/core/Interaction';

export const SPIN_SETTINGS = {
    intensity: 0.001,
    friction: 0.961,
};

export default class Bottle extends ModelGLB {
    tick = 0;
    posZ = 0;
    boxGroup = new WebGLGroup({ rotFactor: .07, zFactor: .1 });
    spinGroup = new WebGLGroup({ rotFactor: 0.08 });

    _spinVelX = 0;
    _spinVelY = 0;
    _spinAccumX = 0;
    _spinAccumY = 0;
    _isMouseDown = false;
    _prevMouseX = 0;
    _prevMouseY = 0;

    constructor(opts = {}) {
        super(opts);
        this.model2 = opts.model2 || null;
        this.diffusion2 = opts.diffusion2 || null;
        this.hasDrag = opts.hasDrag ?? false;
        this.hasFlotation = opts.hasFlotation ?? false;

        this.spinGroup.add(this.boxGroup);
        this.add(this.spinGroup);

        if (this.hasDrag) {
            this._onDown = this._handleDown.bind(this);
            this._onUp = this._handleUp.bind(this);
            this._onMove = this._handleMove.bind(this);

            document.addEventListener(Basics.downEvent, this._onDown);
            document.addEventListener(Basics.upEvent, this._onUp);
            document.addEventListener(Basics.moveEvent, this._onMove);
        }
    }

    _handleDown(e) {
        const mx = isTouch ? e.touches[0].clientX : e.clientX;
        const my = isTouch ? e.touches[0].clientY : e.clientY;
        const { x, y, width, height } = this.dom.getBoundingClientRect();

        if (mx >= x && mx <= x + width && my >= y && my <= y + height) {
            this._isMouseDown = true;
            this._prevMouseX = Interaction.positions.mouse.x;
            this._prevMouseY = Interaction.positions.mouse.y;
            document.body.classList.add('--box-dragging');
        }
    }

    _handleUp() {
        this._isMouseDown = false;
        document.body.classList.remove('--box-dragging');
    }

    _handleMove() {
        if (!this._isMouseDown) return;

        const mx = Interaction.positions.mouse.x;
        const my = Interaction.positions.mouse.y;

        this._spinVelY += (mx - this._prevMouseX) * SPIN_SETTINGS.intensity;

        this._prevMouseX = mx;
        this._prevMouseY = my;
    }

    init() {
        const loadGLB = (src) => new Promise((resolve) => {
            GeometryController.load({ src, call: resolve });
        });

        const jobs = [loadGLB(this.model)];
        if (this.model2) jobs.push(loadGLB(this.model2));

        return Promise.all(jobs).then(([gltf1, gltf2]) => {
            this.object = gltf1;
            this.mesh = gltf1.scene.children[0];
            this.boxGroup.add(gltf1.scene);

            if (gltf2) {
                this.object2 = gltf2;
                // if model2 is separate, take its first child; otherwise fall back to sibling mesh
                this.mesh2 = gltf2.scene.children[0] ?? null;
                this.boxGroup.add(gltf2.scene);
            } else {
                // single GLB with 2 internal meshes
                this.mesh2 = gltf1.scene.children[1] ?? null;
            }

            if (this._material) {
                this._collectMeshes().forEach(m => { m.material = this._material; });
            }

            this.size.copy(this.mesh.scale);
            this.resize();
            this.loadTextures();
        });
    }

    _collectMeshes() {
        const meshes = [];
        if (this.mesh) this.mesh.traverse(o => { if (o.isMesh) meshes.push(o); });
        // If a second GLB was loaded, collect its meshes too (appended after first)
        if (this.object2) {
            this.object2.scene.children[0]?.traverse(o => { if (o.isMesh) meshes.push(o); });
        }
        return meshes;
    }

    loadTextures() {
        const meshes = this._collectMeshes();

        if (this.diffusion && meshes[0]) {
            TexturesController.load({
                src: this.diffusion,
                call: (texture) => {
                    texture.flipY = false;
                    if (!this._material) meshes[0].material = meshes[0].material.clone();
                    meshes[0].material.map = texture;
                    meshes[0].material.needsUpdate = true;
                }
            });
        }

        if (this.diffusion2 && meshes[1]) {
            TexturesController.load({
                src: this.diffusion2,
                call: (texture) => {
                    texture.flipY = false;
                    if (!this._material) meshes[1].material = meshes[1].material.clone();
                    meshes[1].material.map = texture;
                    meshes[1].material.needsUpdate = true;
                }
            });
        }

        if (this.specular) {
            TexturesController.load({
                src: this.specular,
                call: (texture) => {
                    texture.flipY = false;
                    meshes.forEach(o => {
                        if (!this._material) o.material = o.material.clone();
                        o.material.specularMap = texture;
                        o.material.needsUpdate = true;
                    });
                }
            });
        }
    }

    update() {
        this.tick++;

        this._spinVelY *= SPIN_SETTINGS.friction;
        this._spinAccumY += this._spinVelY;
        this.spinGroup.rot.y = this._spinAccumY;
        this.spinGroup.update();

        if (this.hasFlotation) {
            this.boxGroup.rot.x = Math.sin(this.tick * .01) * .15;
            this.boxGroup.rot.y = Math.PI + Math.sin(this.tick * .006) * .15;
            this.boxGroup.rot.z = Math.sin(this.tick * .005) * .08;
            this.boxGroup.pos.x = -2 + Math.sin(this.tick * .01) * 4;
            this.boxGroup.pos.y = -10 + Math.sin(this.tick * .02) * 20;
            this.boxGroup.pos.z = -2 + Math.sin(this.tick * .01) * 4;
        }

        this.boxGroup.update();

        super.update();
    }

    dispose() {
        if (this.hasDrag) {
            document.removeEventListener(Basics.downEvent, this._onDown);
            document.removeEventListener(Basics.upEvent, this._onUp);
            document.removeEventListener(Basics.moveEvent, this._onMove);
        }
        super.dispose();
    }

    resize() {
        super.resize();
        this.dataZ = this.size.x * this.sizeFactor;
    }
}
